import type { Ref } from 'vue';

import { ref } from 'vue';

import { parseLessonShareCodeFromHash } from '~/utils/unified/lessonShareHash';

const shareCodeRef: Ref<string | null> = ref(null);
let hashListenerSubscribed = false;

const syncFromHash = (): void => {
  shareCodeRef.value = parseLessonShareCodeFromHash();
};

export const useLessonShareCode = () => {
  // SSR: window is undefined, listener is skipped, and shareCodeRef keeps its module-default null
  // for every server-rendered request — there's no per-request mutation path on the server, so this
  // module-level ref does not leak state between SSR requests.
  if (typeof window !== 'undefined') {
    if (!hashListenerSubscribed) {
      hashListenerSubscribed = true;
      window.addEventListener('hashchange', syncFromHash);
    }
    // Re-sync on every invocation: cheap, and covers hash mutations that don't fire `hashchange`
    // (history.replaceState, programmatic assignments before the listener was attached).
    syncFromHash();
  }

  const setShareCode = (code: string | null): void => {
    const trimmed = code?.trim() || null;
    shareCodeRef.value = trimmed;
    if (typeof window === 'undefined') {
      return;
    }
    const newHash = trimmed ? `#key=${encodeURIComponent(trimmed)}` : '';
    if (window.location.hash !== newHash) {
      // Only the hash changes here. vue-router keeps its own bookkeeping (scroll positions, the
      // back/forward chain) in the entry's history state, so it is carried over rather than
      // replaced — writing `null` drops it and the router warns on the next navigation.
      history.replaceState(history.state, '', window.location.pathname + window.location.search + newHash);
    }
  };

  return { shareCode: shareCodeRef, setShareCode };
};
